import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatViews, getCategoryName } from "@/utils/catalog";

interface HeroCarouselProps {
  slides: Movie[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (!slides.length) return null;

  const current = slides[index];

  return (
    <section className="w-full">
      <div className="relative h-[460px] w-full overflow-hidden rounded-lg border border-background-200 md:h-[560px]">
        <img
          key={current.id}
          src={current.backdrop || current.cover}
          alt={`${current.title} 焦点剧照`}
          title={`${current.title} - 黄色片免费看推荐影片`}
          className="absolute inset-0 h-full w-full animate-slow-zoom object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/70 to-foreground-950/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />

        <div className="relative flex h-full w-full flex-col justify-end p-6 md:p-12">
          <div className="max-w-[640px]">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-foreground-950">
              <i className="ri-fire-line" /> 本周焦点推荐
            </span>
            <h2 className="mt-3 font-heading text-2xl font-semibold leading-tight text-background-50 md:text-4xl">
              {current.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-background-50/80 md:text-sm">
              <span className="text-accent-300">{current.rating.toFixed(1)} 分</span>
              <span>{getCategoryName(current.category)}</span>
              <span>{current.year}</span>
              <span>{current.region}</span>
              <span>{current.duration}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" /> {formatViews(current.views)}
              </span>
            </div>
            <p className="mt-3 hidden max-w-[560px] text-sm leading-relaxed text-background-50/80 sm:block">
              {current.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-base" /> 查看影片详情
              </Link>
              <Link
                to={`/category/${current.category}`}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-4 py-2.5 text-sm text-background-50 transition-colors hover:border-background-50/80 hover:bg-background-50/10"
              >
                浏览同类影片
              </Link>
            </div>
          </div>

          <div className="absolute right-5 top-1/2 hidden -translate-y-1/2 flex-col items-center gap-2 md:flex">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => setIndex((prev) => (prev - 1 + slides.length) % slides.length)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => setIndex((prev) => (prev + 1) % slides.length)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          <div className="absolute right-6 top-6 flex items-center gap-1.5 md:bottom-8 md:right-8 md:top-auto">
            {slides.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到 ${item.title}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  i === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}